import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PrintIcon from '@mui/icons-material/Print';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import { euro, germanDate, num } from '../lib/euerFormat';
import { PageHelp } from '../components/PageHelp';
import { EuerTable, Line, type EuerData } from '../components/EuerTable';

interface KstResponse {
  kst: {
    year: number;
    euer: EuerData & { from: string; to: string };
    quarters: { quarter: string; cents: number }[];
    grossIncome: number;
    internalIncome: number;
    thresholdExceeded: boolean;
    thresholdCents: number;
    allowanceCents: number;
    taxableCents: number;
  };
  dataAsOf: string | null;
}

/** "2025-Q4" -> "4. Quartal 2025" */
const quarterLabel = (q: string) => `${q.slice(6)}. Quartal ${q.slice(0, 4)}`;

/**
 * D-25: Grundlage der Körperschaftsteuererklärung -- EÜR des wirtschaftlichen
 * Geschäftsbetriebs (alle Abteilungen) mit berechneten USt-Zahlungen/-Erstattungen,
 * Besteuerungsgrenze und Freibetrag. Admin und Vorstand.
 */
export default function KstPage() {
  const today = new Date().toISOString().slice(0, 10);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear - 1);
  const [data, setData] = useState<KstResponse>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    api
      .get<KstResponse>(`/admin/report/kst?year=${year}`)
      .then(setData)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Auswertung konnte nicht geladen werden'),
      )
      .finally(() => setLoading(false));
  }, [year]);

  function print() {
    const title = document.title;
    document.title = `KSt-Grundlage ${year}`;
    window.print();
    document.title = title;
  }

  const k = data?.kst.year === year ? data.kst : undefined;
  const paidTotal = k?.quarters.reduce((s, q) => s + q.cents, 0) ?? 0;
  return (
    <Stack
      spacing={3}
      sx={{
        '@media print': {
          '& .MuiPaper-root': { boxShadow: 'none' },
          '& tr': { breakInside: 'avoid' },
          '& .kst-break': { breakBefore: 'page' },
        },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">KSt-Erklärung</Typography>
        <Box sx={{ flex: 1, displayPrint: 'none' }}>
          <PageHelp text="Grundlage der Körperschaftsteuererklärung: die EÜR des wirtschaftlichen Geschäftsbetriebs aller Abteilungen (Kostenstellen mit Bereich WB). Die Zahlungen an das und Erstattungen vom Finanzamt gelten für den ganzen Verein und kommen ein Quartal später; deshalb rechnet MyBudget sie für den Wirtschaftsbetrieb aus den Belegen nach. Dazu Besteuerungsgrenze und Freibetrag. Keine Steuerberechnung – die Erklärung macht der Steuerberater." />
        </Box>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={print}
          disabled={!k}
          sx={{ displayPrint: 'none' }}
        >
          Drucken
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ displayPrint: 'none' }}>
        <TextField
          select
          size="small"
          label="Jahr"
          value={year}
          onChange={(ev) => setYear(Number(ev.target.value))}
          sx={{ minWidth: 110 }}
        >
          {Array.from({ length: thisYear - 2017 }, (_, i) => thisYear - i).map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {data && (
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ display: 'none', displayPrint: 'inline' }}>
            Sp.Vgg. Lagerlechfeld-Graben · Wirtschaftlicher Geschäftsbetrieb ·{' '}
          </Box>
          {k && `${germanDate(k.euer.from)} bis ${germanDate(k.euer.to)} · `}Datenstand:{' '}
          {data.dataAsOf ? formatTimestamp(data.dataAsOf) : 'noch kein Import'}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {k && year === thisYear && (
        <Alert severity="info">
          Das Jahr läuft noch – die Zahlen gelten bis heute, das 3. Quartal ist ggf. noch nicht
          vollständig.
        </Alert>
      )}
      {loading && !k && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {k && (
        <Paper sx={{ p: 2, opacity: loading ? 0.5 : 1 }}>
          <Typography variant="h6">Besteuerungsgrenze (§ 64 Abs. 3 AO)</Typography>
          <Table size="small">
            <TableBody>
              <Line
                label="Einnahmen des Wirtschaftsbetriebs einschließlich Umsatzsteuer"
                cents={k.grossIncome}
              />
              <Line label="Besteuerungsgrenze" cents={k.thresholdCents} />
            </TableBody>
          </Table>
          <Alert severity={k.thresholdExceeded ? 'warning' : 'success'} sx={{ mt: 1 }}>
            {k.thresholdExceeded
              ? 'Die Besteuerungsgrenze ist überschritten – Körperschaft- und Gewerbesteuer sind zu prüfen.'
              : 'Die Besteuerungsgrenze ist nicht überschritten – es fallen keine Körperschaft- und Gewerbesteuer an.'}
          </Alert>
          {k.internalIncome !== 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Nicht mitgezählt: Einnahmen aus Umbuchungen (UM…) anderer Bereiche oder Abteilungen
              von {euro(k.internalIncome)} – das sind keine Einnahmen von Dritten. In der EÜR unten
              sind sie enthalten.
            </Typography>
          )}
        </Paper>
      )}

      {k && (
        <EuerTable
          e={k.euer}
          loading={loading}
          refundLabel="Erstattungen des Finanzamts (berechnet, Wirtschaftsbetrieb)"
          paymentLabel="Umsatzsteuerzahlungen an das Finanzamt (berechnet, Wirtschaftsbetrieb)"
        />
      )}

      {k && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Freibetrag (§ 24 KStG)</Typography>
          <Table size="small">
            <TableBody>
              <Line label={k.euer.result < 0 ? 'Verlust' : 'Gewinn'} cents={k.euer.result} />
              <Line label="− Freibetrag" cents={-k.allowanceCents} />
              <Line
                label="= Verbleibender Betrag (Grundlage der Körperschaftsteuer)"
                cents={k.taxableCents}
                bold
              />
            </TableBody>
          </Table>
          {!k.thresholdExceeded && (
            <Typography variant="caption" color="text.secondary">
              Unter der Besteuerungsgrenze bleibt nichts zu versteuern.
            </Typography>
          )}
        </Paper>
      )}

      {k && (
        <Paper className="kst-break" sx={{ p: 2 }}>
          <Typography variant="h6">Umsatzsteuer des Wirtschaftsbetriebs je Voranmeldung</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Die Umsatzsteuer-Voranmeldungen gelten für den ganzen Verein – Wirtschaftsbetrieb und
            Zweckbetrieb zusammen – und werden jeweils im Folgequartal gezahlt bzw. erstattet: die
            des 4. Quartals des Vorjahres im Januar, die des 3. Quartals im Oktober. In der
            KSt-Erklärung zählt nur der Anteil des Wirtschaftsbetriebs. MyBudget rechnet ihn aus den
            Belegen des Wirtschaftsbetriebs: vereinnahmte Umsatzsteuer abzüglich gezahlter
            Vorsteuer, je Quartal nach Belegdatum wie in der Voranmeldung, einschließlich noch nicht
            bezahlter Belege. Die tatsächlichen Zahlungen und Erstattungen des Vereins ersetzt
            dieser Betrag.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Voranmeldung</TableCell>
                <TableCell {...num}>Erstattung (+) / Zahlung (−)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {k.quarters.map((q) => (
                <TableRow key={q.quarter}>
                  <TableCell>{quarterLabel(q.quarter)}</TableCell>
                  <TableCell {...num}>{euro(q.cents)}</TableCell>
                </TableRow>
              ))}
              <Line label={`Summe im Jahr ${k.year}`} cents={paidTotal} bold />
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Die Voranmeldung des 4. Quartals {k.year} wird erst {k.year + 1} gezahlt bzw. erstattet
            und zählt dort. Nachzahlungen oder Erstattungen aus der Umsatzsteuer-Jahreserklärung
            sind nicht berücksichtigt.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
